import { useState, useEffect } from 'react';
import {
  Plus, MoreHorizontal, MessageSquare, Paperclip, Zap, ArrowLeftRight, Search, X
} from 'lucide-react';
import api from '../api';
import { cn } from '@/lib/utils';

// Backend `status` is a free-text field with only 4 real values in practice
// ("New" / "Contacted" / "Replied" / "Closed" — see leads_finder/models/lead.py),
// and it's also what GET /leads/stats counts by. Our board still shows 5 stages,
// so "Proposal Sent" / "Proposal Declined" both write back status="Contacted"
// (col.writeStatus) rather than a made-up literal — keeps backend stats accurate.
// Which of those two sub-stages a lead is *displayed* in is derived in
// getColumnKey() below; "Replied" leads are folded into the "Contacted" column
// since there's no dedicated column for it.
const COLUMNS = [
  { id: 'new', title: 'New Leads', color: 'bg-blue-500', writeStatus: 'New' },
  { id: 'contacted', title: 'Contacted', color: 'bg-orange-500', writeStatus: 'Contacted' },
  { id: 'proposal_sent', title: 'Proposal Sent', color: 'bg-purple-500', writeStatus: 'Contacted' },
  { id: 'proposal_declined', title: 'Proposal Declined', color: 'bg-rose-500', writeStatus: 'Contacted' },
  { id: 'closed', title: 'Closed Won', color: 'bg-green-500', writeStatus: 'Closed' },
];

// Maps a lead onto one of the 5 UI columns from its real backend status.
// Note: "Proposal Sent" is derived from an actual signal (a proposal exists on
// the lead) so it survives a refresh. "Proposal Declined" has no backing field
// on the backend today — moving a lead there is a one-time action for now; on
// the next refetch it settles back into "Contacted"/"Proposal Sent" like any
// other contacted lead, since status alone can't tell "declined" apart from
// "contacted, proposal sent". Flag to backend if this needs to persist.
function getColumnKey(lead) {
  if (lead.status === 'New') return 'new';
  if (lead.status === 'Closed') return 'closed';
  if (lead.status === 'Contacted' || lead.status === 'Replied') {
    return lead.proposals?.length > 0 ? 'proposal_sent' : 'contacted';
  }
  return 'contacted';
}

export default function CRMPipeline() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newLeadName, setNewLeadName] = useState('');
  const [newLeadAddress, setNewLeadAddress] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    try {
      const response = await api.get('/leads/crm-pipeline');
      setLeads(response.data);
    } catch (err) {
      console.error('Failed to fetch leads', err);
    } finally {
      setLoading(false);
    }
  };

  // Soft-remove: flips added_to_crm back to false via the toggle endpoint
  // (lead itself is not deleted, it just drops out of this filtered view).
  const removeFromPipeline = async (leadId) => {
    try {
      await api.patch(`/leads/${leadId}/crm-pipeline`);
      setLeads((prev) => prev.filter((l) => l._id !== leadId));
    } catch (err) {
      console.error('Failed to remove lead from pipeline', err);
      alert('Failed to remove lead from pipeline: ' + (err.response?.data?.detail || err.message));
    }
  };

  const moveLead = async (leadId, targetColId) => {
    const targetCol = COLUMNS.find((c) => c.id === targetColId);
    if (!targetCol) return;
    try {
      await api.patch(`/leads/${leadId}/status?status=${encodeURIComponent(targetCol.writeStatus)}`);
      fetchLeads();
    } catch (err) {
      console.error('Failed to move lead', err);
    }
  };

  const handleAddLead = async () => {
    if (!newLeadName) return;
    try {
      const response = await api.post('/leads/', {
        name: newLeadName,
        address: newLeadAddress,
        location: { lat: 0, lng: 0 },
        place_id: `manual_${Date.now()}`,
        business_type: "manual",
        city: "manual",
        status: "New"
      });
      // New leads default to added_to_crm=false — flip it on so it actually
      // shows up here (this page now only lists added_to_crm=true leads).
      const createdId = response.data?._id;
      if (createdId) {
        try {
          await api.patch(`/leads/${createdId}/crm-pipeline`);
        } catch (toggleErr) {
          console.error('Failed to add manual lead to CRM pipeline', toggleErr);
        }
      }
      setIsAddModalOpen(false);
      setNewLeadName('');
      setNewLeadAddress('');
      fetchLeads();
    } catch (err) {
      console.error('Failed to create lead', err);
    }
  };

  const visibleLeads = leads.filter((lead) =>
    !search || (lead.name || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="p-8 h-full flex flex-col overflow-hidden">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white">CRM Pipeline</h1>
          <p className="text-slate-400">Track your outreach and conversion progress</p>
        </div>
        <button onClick={() => setIsAddModalOpen(true)} className="bg-white/[0.04] border border-white/[0.08] hover:bg-white/[0.07] text-white px-4 py-2 rounded-xl font-bold flex items-center gap-2 transition-all">
          <Plus size={18} /> Add Lead
        </button>
      </div>

      <div className="mb-6 relative group max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-indigo-400 transition-colors" size={16} />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search leads by name..."
          className="w-full bg-white/[0.03] border border-white/[0.08] rounded-xl py-2.5 pl-9 pr-3 text-sm outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all text-white"
        />
      </div>

      <div className="flex-1 flex gap-6 overflow-x-auto pb-4">
        {COLUMNS.map((col) => (
          <div key={col.id} className="min-w-[320px] w-[320px] flex flex-col">
            <div className="flex items-center justify-between mb-4 px-2">
              <div className="flex items-center gap-2">
                <div className={cn("w-2 h-2 rounded-full", col.color)} />
                <h3 className="font-bold text-slate-300">{col.title}</h3>
                <span className="bg-white/[0.08] px-2 py-0.5 rounded-md text-xs text-slate-300 font-bold">
                  {visibleLeads.filter((l) => getColumnKey(l) === col.id).length}
                </span>
              </div>
              <button className="text-slate-500 hover:text-slate-300 transition-colors">
                <MoreHorizontal size={18} />
              </button>
            </div>

            <div className="flex-1 bg-white/[0.02] border border-white/[0.06] rounded-2xl p-3 space-y-3 overflow-y-auto">
              {visibleLeads.filter((l) => getColumnKey(l) === col.id).map((lead) => (
                <div key={lead._id} className="bg-white/[0.03] p-4 rounded-xl border border-white/[0.08] hover:bg-white/[0.05] hover:border-white/[0.12] transition-all cursor-grab active:cursor-grabbing group relative">
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="font-bold text-sm text-white leading-tight pr-4">{lead.name}</h4>
                    {lead.proposals?.length > 0 && <Zap size={14} className="text-amber-400 fill-amber-400 flex-shrink-0" />}
                  </div>
                  <p className="text-[11px] text-slate-500 mb-4 line-clamp-2">{lead.address}</p>

                  <div className="flex items-center justify-between mt-4">
                    <div className="flex -space-x-2">
                      <div className="w-6 h-6 rounded-full bg-white/[0.06] border border-white/[0.08] flex items-center justify-center text-[10px] font-bold text-slate-300">
                        {lead.owner_name ? lead.owner_name.split(' ').map((n) => n[0]).join('') : 'U'}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 text-slate-500">
                      <div className="flex items-center gap-1">
                        <MessageSquare size={12} />
                        <span className="text-[10px] font-medium">{lead.notes?.length || 0}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Paperclip size={12} />
                        <span className="text-[10px] font-medium">{lead.proposals?.length || 0}</span>
                      </div>
                    </div>
                  </div>

                  <div className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col gap-1">
                    {COLUMNS.map((targetCol) => (
                      targetCol.id !== col.id && (
                        <button key={targetCol.id} onClick={() => moveLead(lead._id, targetCol.id)}
                          className="bg-slate-900 hover:bg-slate-800 text-slate-300 p-1.5 rounded-lg border border-white/[0.08] transition-all"
                          title={`Move to ${targetCol.title}`}>
                          <ArrowLeftRight size={14} />
                        </button>
                      )
                    ))}
                    <button onClick={() => removeFromPipeline(lead._id)}
                      className="bg-slate-900 hover:bg-rose-950/60 text-slate-300 hover:text-rose-400 p-1.5 rounded-lg border border-white/[0.08] transition-all"
                      title="Remove from Pipeline">
                      <X size={14} />
                    </button>
                  </div>
                </div>
              ))}

              {visibleLeads.filter((l) => getColumnKey(l) === col.id).length === 0 && (
                <div className="h-24 border-2 border-dashed border-white/[0.08] rounded-xl flex items-center justify-center bg-white/[0.01]">
                  <p className="text-xs font-semibold text-slate-600">Empty</p>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {isAddModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 backdrop-blur-sm">
          <div className="bg-slate-900 border border-white/[0.08] p-6 rounded-2xl w-full max-w-md shadow-2xl">
            <h2 className="text-xl font-bold text-white mb-4">Add Manual Lead</h2>
            <div className="space-y-4 mb-6">
              <div>
                <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Business Name</label>
                <input autoFocus type="text" value={newLeadName} onChange={(e) => setNewLeadName(e.target.value)}
                  className="w-full mt-1.5 px-4 py-2.5 bg-white/[0.03] border border-white/[0.08] rounded-xl outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all text-sm text-white" />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Address</label>
                <input type="text" value={newLeadAddress} onChange={(e) => setNewLeadAddress(e.target.value)}
                  className="w-full mt-1.5 px-4 py-2.5 bg-white/[0.03] border border-white/[0.08] rounded-xl outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all text-sm text-white" />
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => setIsAddModalOpen(false)} className="px-4 py-2 hover:bg-white/[0.05] text-slate-300 rounded-xl font-bold transition-colors">Cancel</button>
              <button onClick={handleAddLead} className="bg-indigo-600 hover:bg-indigo-500 text-white px-6 py-2 rounded-xl font-bold transition-all">Create</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
